import { Check, Clock, ListChecks } from 'lucide-react';
import { DetailPanel } from '@/components/common/DetailPanel';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { formatDisplayDateTime } from '@/utils/date';
import type { FeedbackListItem } from '../types/feedback.types';
import { formatFeedbackEntryTime } from '../utils/feedbackEntryTime';

const NOT_YET_TEXT = 'Not yet';

interface TimelineStep {
  key: string;
  label: string;
  when: string | null;
  who: string | null | undefined;
}

function getTimelineSteps(feedback: FeedbackListItem | undefined): TimelineStep[] {
  return [
    {
      key: 'entered',
      label: 'Entered',
      when: feedback ? formatFeedbackEntryTime(feedback) : null,
      who: feedback?.user?.fullName,
    },
    {
      key: 'read',
      label: 'Read',
      when: feedback?.readAt ? formatDisplayDateTime(feedback.readAt) : null,
      who: null,
    },
    {
      key: 'completed',
      label: 'Completed',
      when: feedback?.completedAt ? formatDisplayDateTime(feedback.completedAt) : null,
      who: feedback?.completedByName,
    },
  ];
}

interface TimelineStepItemProps {
  step: TimelineStep;
  isLast: boolean;
  isLoading: boolean;
}

function TimelineStepItem({ step, isLast, isLoading }: TimelineStepItemProps) {
  const isDone = step.when !== null;
  const Icon = isDone ? Check : Clock;
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {isLast ? null : (
        <span
          className={cn(
            'absolute top-8 bottom-0 left-3.5 w-px',
            isDone ? 'bg-primary/40' : 'bg-border',
          )}
          aria-hidden
        />
      )}
      <span
        className={cn(
          'relative flex size-7 shrink-0 items-center justify-center rounded-full',
          isDone ? ACCENT_MARK_CLASS : 'bg-card text-muted-foreground ring-1 ring-border',
        )}
        aria-hidden
      >
        <Icon className="size-3.5" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5 pt-0.5">
        <span className="text-sm font-semibold">{step.label}</span>
        {isLoading ? (
          <Skeleton className="mt-1 h-4 w-36" />
        ) : (
          <span className={cn('text-sm tabular-nums', !isDone && 'text-muted-foreground')}>
            {step.when ?? NOT_YET_TEXT}
          </span>
        )}
        {step.who ? (
          <span className="text-xs wrap-break-word text-muted-foreground">by {step.who}</span>
        ) : null}
      </div>
    </li>
  );
}

interface FeedbackProgressTimelineProps {
  feedback: FeedbackListItem | undefined;
}

export function FeedbackProgressTimeline({ feedback }: FeedbackProgressTimelineProps) {
  const steps = getTimelineSteps(feedback);
  return (
    <DetailPanel title="Progress" icon={ListChecks}>
      <ol>
        {steps.map((step, index) => (
          <TimelineStepItem
            key={step.key}
            step={step}
            isLast={index === steps.length - 1}
            isLoading={feedback === undefined}
          />
        ))}
      </ol>
    </DetailPanel>
  );
}
